import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock3,
  ExternalLink,
  Inbox,
  Loader2,
  Mail,
  MessageCircle,
  RefreshCw,
  Search,
  Send,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../context/AuthContext';
import { useWorkspaceWindow } from '../../context/WorkspaceWindowContext';
import API_BASE from '../../utils/apiBase';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import PersonMessageComposer from '../../components/portal/PersonMessageComposer';
import WhatsAppQuarantinePanel from '../../components/portal/WhatsAppQuarantinePanel';
import WhatsAppAttachment from '../../components/portal/WhatsAppAttachment';
import {
  isActivatedStaff,
  recruitmentInboxPollInterval,
  isRecruitmentThread,
  recruitmentMessageState,
  recruitmentWhatsAppFailure,
  renderRecruitmentTemplate,
} from './recruitmentInboxHelpers';

const API = API_BASE;
const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'unread', label: 'Unread' },
];

function fmtWhen(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function displayName(thread) {
  return thread?.company_name || thread?.contact_name || thread?.contact_email || 'Applicant';
}

function threadStatus(thread) {
  return String(thread?.status || (thread?.closed_at ? 'closed' : 'open')).toLowerCase();
}

function channelLabel(message) {
  return String(message?.channel || 'email').toLowerCase() === 'whatsapp' ? 'WhatsApp' : 'Email';
}

function Badge({ children, tone = 'slate' }) {
  const tones = {
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-800',
    red: 'border-red-200 bg-red-50 text-red-700',
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

function MessageCard({ message }) {
  const outbound = message?.direction === 'outbound';
  const channel = channelLabel(message);
  const state = recruitmentMessageState(message);
  const isWhatsApp = channel === 'WhatsApp';
  const failure = recruitmentWhatsAppFailure(message);
  const toEmail = Array.isArray(message?.to_email) ? message.to_email[0] : message?.to_email;

  return (
    <article className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
      <div className={`w-full max-w-[760px] rounded-lg border p-4 shadow-sm ${
        outbound ? 'border-primary/20 bg-primary/5' : 'border-slate-200 bg-white'
      }`}>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-2 border-b border-slate-100 pb-2">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-slate-950">
                {outbound ? 'Osabea Recruitment' : 'Applicant'}
              </p>
              <Badge tone={isWhatsApp ? 'green' : 'blue'}>{channel}</Badge>
              <Badge tone={state.tone}>{state.label}</Badge>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              {isWhatsApp
                ? (outbound
                  ? (message?.send_mode === 'manual_support_reply' ? 'Human support reply' : 'Approved Meta template')
                  : 'Applicant WhatsApp message')
                : (outbound
                  ? `To ${toEmail || 'applicant'}`
                  : `From ${message?.from_email || 'applicant'}`)}
            </p>
          </div>
          <span className="flex items-center gap-1 text-xs text-slate-500">
            <Clock3 className="h-3.5 w-3.5" />
            {fmtWhen(message?.created_at)}
          </span>
        </div>

        {message?.subject ? <p className="mb-2 text-sm font-semibold text-slate-900">{message.subject}</p> : null}
        {failure ? (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-900">
            <p className="font-semibold">Meta did not deliver this WhatsApp message.</p>
            {failure.recipient ? (
              <p className="mt-1">Destination: <strong>+{failure.recipient}</strong></p>
            ) : null}
            <p className="mt-1">
              {failure.code ? `Meta error ${failure.code}: ` : ''}{failure.reason}
            </p>
            <p className="mt-1">
              Check that the saved applicant number is the correct WhatsApp number before retrying. The message text below is the intended message, not proof of delivery.
            </p>
          </div>
        ) : isWhatsApp && outbound && message?.to_phone ? (
          <p className="mb-3 text-xs text-slate-500">WhatsApp destination: +{message.to_phone}</p>
        ) : null}

        <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">
          {message?.body_text || 'No message body stored.'}
        </p>
        <WhatsAppAttachment message={message} />

        {message?.action_label ? (
          <div className="mt-3 rounded-md border border-slate-200 bg-white/80 p-2 text-xs text-slate-600">
            CareOps action: <strong>{message.action_label}</strong>
          </div>
        ) : null}
      </div>
    </article>
  );
}

function ThreadRow({ thread, selected, onSelect }) {
  const unread = Number(thread?.unread_count || 0) > 0;
  return (
    <button
      type="button"
      onClick={() => onSelect(thread.id)}
      className={`w-full border-b border-slate-100 px-4 py-3 text-left transition-colors ${
        selected ? 'bg-primary/5 shadow-[inset_3px_0_0_var(--color-primary,#0d4f3c)]' : 'bg-white hover:bg-slate-50'
      }`}
    >
      <div className="flex items-start gap-3">
        <span className={`mt-1.5 h-2.5 w-2.5 rounded-full ${unread ? 'bg-primary' : 'bg-slate-300'}`} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <p className={`truncate text-sm ${unread ? 'font-bold text-slate-950' : 'font-semibold text-slate-800'}`}>
              {displayName(thread)}
            </p>
            <span className="shrink-0 text-[11px] text-slate-500">{fmtWhen(thread.last_message_at || thread.updated_at)}</span>
          </div>
          <p className="mt-0.5 truncate text-sm font-medium text-slate-700">{thread.subject || 'Recruitment conversation'}</p>
          <p className="mt-1 line-clamp-2 text-xs leading-5 text-slate-500">{thread.last_preview || 'No preview yet'}</p>
          <div className="mt-2 flex items-center gap-2">
            <Badge tone={threadStatus(thread) === 'closed' ? 'slate' : 'green'}>
              {threadStatus(thread) === 'closed' ? 'Closed' : 'Open'}
            </Badge>
            {unread ? <Badge tone="red">{thread.unread_count} unread</Badge> : null}
          </div>
        </div>
      </div>
    </button>
  );
}

export default function RecruitmentInboxPage() {
  const { token, isManagerOrAdmin } = useAuth();
  const workspaceWindow = useWorkspaceWindow();
  const [searchParams, setSearchParams] = useSearchParams();
  const threadParam = searchParams.get('thread');
  const managerAccess = isManagerOrAdmin();

  const [threads, setThreads] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [applicant, setApplicant] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [showAttention, setShowAttention] = useState(false);

  const [mode, setMode] = useState('manual');
  const [selectedEmailTemplate, setSelectedEmailTemplate] = useState('');
  const [guidedSubject, setGuidedSubject] = useState('');
  const [guidedBody, setGuidedBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendNotice, setSendNotice] = useState(null);

  const detailRequestRef = useRef(0);

  const loadThreads = useCallback(async ({ silent = false } = {}) => {
    if (!token) return;
    if (!silent) setListLoading(true);
    setListError('');
    try {
      const response = await fetch(`${API}/portal-inbox/threads?department=recruitment&status=all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(response.status === 403 ? 'Manager or admin access required.' : 'Recruitment Inbox could not be loaded.');
      const data = await response.json();
      setThreads((data.threads || []).filter(isRecruitmentThread));
    } catch (error) {
      if (!silent) setListError(error.message || 'Recruitment Inbox could not be loaded.');
    } finally {
      if (!silent) setListLoading(false);
    }
  }, [token]);

  const loadCommunicationOptions = useCallback(async () => {
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const [templatesResult, statusResult] = await Promise.allSettled([
      fetch(`${API}/onboarding-communications/templates`, { headers }),
      fetch(`${API}/onboarding-communications/whatsapp/status`, { headers }),
    ]);

    if (templatesResult.status === 'fulfilled' && templatesResult.value.ok) {
      const payload = await templatesResult.value.json();
      setTemplates(Array.isArray(payload.templates) ? payload.templates : []);
    }
    if (statusResult.status === 'fulfilled' && statusResult.value.ok) {
      setWhatsappStatus(await statusResult.value.json());
    } else {
      setWhatsappStatus(null);
    }
  }, [token]);

  const refreshThreadDeliveryStatus = useCallback(async (threadId) => {
    if (!threadId || !token) return;
    try {
      // A previous inbound webhook may have been quarantined when duplicate person
      // records shared this phone. Recover only replies whose recent outbound
      // Recruitment thread is unique, then load the conversation normally.
      try {
        await fetch(`${API}/onboarding-communications/whatsapp/quarantine/recover-safe`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // Recovery is best-effort and must never make the inbox unavailable.
      }

      const response = await fetch(`${API}/portal-inbox/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      if (!isRecruitmentThread(data.thread)) return;
      setThread((current) => (current?.id === threadId ? data.thread : current));
      setMessages(data.messages || []);
      loadThreads({ silent: true });
    } catch {
      // Delivery refresh is best-effort; the normal page controls remain usable.
    }
  }, [loadThreads, token]);

  const loadThread = useCallback(async (threadId) => {
    if (!threadId || !token) return;
    const requestId = detailRequestRef.current + 1;
    detailRequestRef.current = requestId;
    setDetailLoading(true);
    setDetailError('');
    setApplicant(null);
    setSendNotice(null);

    try {
      const response = await fetch(`${API}/portal-inbox/threads/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Recruitment conversation could not be loaded.');
      const data = await response.json();
      if (detailRequestRef.current !== requestId) return;
      if (!isRecruitmentThread(data.thread)) throw new Error('This conversation does not belong to Recruitment.');

      setThread(data.thread);
      setMessages(data.messages || []);
      setThreads((current) => current.map((item) => item.id === threadId ? { ...item, unread_count: 0 } : item));

      if (data.thread?.entity_id) {
        const applicantResponse = await fetch(`${API}/employees/${data.thread.entity_id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (detailRequestRef.current !== requestId) return;
        if (applicantResponse.ok) setApplicant(await applicantResponse.json());
      }
      loadThreads({ silent: true });
    } catch (error) {
      if (detailRequestRef.current !== requestId) return;
      setThread(null);
      setMessages([]);
      setApplicant(null);
      setDetailError(error.message || 'Recruitment conversation could not be loaded.');
    } finally {
      if (detailRequestRef.current === requestId) setDetailLoading(false);
    }
  }, [loadThreads, token]);

  useEffect(() => {
    if (!managerAccess) return;
    loadThreads();
    loadCommunicationOptions();
  }, [loadCommunicationOptions, loadThreads, managerAccess]);

  useEffect(() => {
    if (!managerAccess || !threadParam) return undefined;
    const pollIntervalMs = recruitmentInboxPollInterval(messages);
    const timer = window.setInterval(() => {
      refreshThreadDeliveryStatus(threadParam);
    }, pollIntervalMs);
    return () => window.clearInterval(timer);
  }, [managerAccess, messages, refreshThreadDeliveryStatus, threadParam]);

  useEffect(() => {
    if (!threadParam) {
      detailRequestRef.current += 1;
      setThread(null);
      setMessages([]);
      setApplicant(null);
      setDetailError('');
      return;
    }
    loadThread(threadParam);
  }, [loadThread, threadParam]);

  useEffect(() => {
    setMode('manual');
    setSelectedEmailTemplate('');
    setGuidedSubject('');
    setGuidedBody('');
    setSendNotice(null);
  }, [thread?.id]);

  useEffect(() => {
    if (!selectedEmailTemplate) {
      setGuidedSubject('');
      setGuidedBody('');
      return;
    }
    const selected = templates.find((item) => item.key === selectedEmailTemplate);
    const rendered = renderRecruitmentTemplate(selected, applicant);
    setGuidedSubject(rendered.subject);
    setGuidedBody(rendered.body);
  }, [applicant, selectedEmailTemplate, templates]);

  const filteredThreads = useMemo(() => {
    const query = search.trim().toLowerCase();
    return threads
      .filter((item) => {
        if (!isRecruitmentThread(item)) return false;
        if (filter === 'unread' && !(Number(item.unread_count || 0) > 0)) return false;
        if (filter === 'open' && threadStatus(item) !== 'open') return false;
        if (!query) return true;
        return [displayName(item), item.contact_email, item.subject, item.last_preview]
          .join(' ')
          .toLowerCase()
          .includes(query);
      })
      .sort((a, b) => new Date(b.last_message_at || b.updated_at || 0) - new Date(a.last_message_at || a.updated_at || 0));
  }, [filter, search, threads]);

  const manualWhatsAppReady = whatsappStatus?.manual_support_ready === true;
  const activated = isActivatedStaff(applicant);
  const canSendApplicantCommunication = Boolean(thread?.entity_id && applicant && !activated);

  const selectThread = (threadId) => {
    const next = new URLSearchParams();
    next.set('department', 'recruitment');
    next.set('thread', threadId);
    setSearchParams(next);
  };

  const closeThread = () => {
    detailRequestRef.current += 1;
    const next = new URLSearchParams();
    next.set('department', 'recruitment');
    setSearchParams(next);
    setThread(null);
    setMessages([]);
    setApplicant(null);
  };

  const sendGuidedEmail = async () => {
    if (!canSendApplicantCommunication || !selectedEmailTemplate || !guidedSubject.trim() || !guidedBody.trim() || sending) return;
    setSending(true);
    setSendNotice(null);
    try {
      const response = await fetch(`${API}/employees/${thread.entity_id}/communications/send`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_key: selectedEmailTemplate,
          subject: guidedSubject.trim(),
          body: guidedBody.trim(),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.detail || 'Onboarding email could not be sent.');
      if (payload.message) setMessages((current) => [...current.filter((item) => item.id !== payload.message.id), payload.message]);
      setSendNotice({
        type: 'success',
        text: payload.action_label
          ? `Guided recruitment email sent. Secure CareOps action: ${payload.action_label}.`
          : 'Guided recruitment email sent.',
      });
      toast.success('Guided recruitment email sent');
      loadThreads({ silent: true });
    } catch (error) {
      setSendNotice({ type: 'error', text: error.message || 'Onboarding email could not be sent.' });
      toast.error(error.message || 'Onboarding email could not be sent');
    } finally {
      setSending(false);
    }
  };



  if (!managerAccess) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
        Recruitment Inbox is restricted to managers and administrators.
      </div>
    );
  }

  const shellClass = workspaceWindow
    ? 'min-h-0 space-y-4 md:flex md:h-full md:flex-col md:overflow-hidden'
    : 'min-h-[calc(100vh-140px)] space-y-4 md:flex md:h-[calc(100vh-140px)] md:flex-col md:overflow-hidden';
  const selectedTemplate = templates.find((item) => item.key === selectedEmailTemplate);
  const unreadCount = threads.reduce((total, item) => total + Number(item.unread_count || 0), 0);

  return (
    <div className={shellClass} data-testid="recruitment-inbox-page">
      <div className="shrink-0 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
              <UserCheck className="h-4 w-4" /> Recruitment
            </p>
            <h1 className="mt-1 text-xl font-semibold text-slate-950">Recruitment Inbox</h1>
            <p className="mt-1 text-sm text-slate-600">
              Applicant communications only — human support for the recruitment journey. Email can be initiated by Osabea; human-written WhatsApp replies are available after the applicant messages first.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {unreadCount > 0 ? <Badge tone="red">{unreadCount} unread</Badge> : null}
            <Badge tone={manualWhatsAppReady ? 'green' : 'amber'}>
              <MessageCircle className="mr-1 h-3 w-3" />
              {manualWhatsAppReady ? 'WhatsApp connected' : 'WhatsApp unavailable'}
            </Badge>
            <Button type="button" variant="outline" size="sm" onClick={() => setShowAttention((value) => !value)}>
              <AlertTriangle className="mr-1.5 h-4 w-4" />
              WhatsApp issues
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/portal/recruitment">Recruitment pipeline</Link>
            </Button>
          </div>
        </div>
      </div>

      {showAttention ? <WhatsAppQuarantinePanel /> : null}

      <div className="grid gap-4 md:min-h-0 md:flex-1 md:grid-cols-[minmax(280px,380px)_minmax(0,1fr)] md:overflow-hidden">
        <section className={`${thread ? 'hidden md:flex' : 'flex'} min-h-0 flex-col rounded-lg border border-slate-200 bg-white shadow-sm md:h-full`}>
          <div className="shrink-0 border-b border-slate-200 p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Applicants</p>
                <p className="text-sm font-semibold text-slate-900">{filteredThreads.length} conversation{filteredThreads.length === 1 ? '' : 's'}</p>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => loadThreads()} disabled={listLoading}>
                <RefreshCw className={`h-4 w-4 ${listLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <div className="mt-3 flex gap-1 overflow-x-auto">
              {FILTERS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setFilter(item.id)}
                  className={`shrink-0 rounded-md border px-3 py-1.5 text-xs font-semibold ${
                    filter === item.id ? 'border-primary bg-primary text-white' : 'border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <label className="mt-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <Search className="h-4 w-4 text-slate-400" />
              <span className="sr-only">Search applicants</span>
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search applicant, email or subject"
                className="w-full bg-transparent text-sm outline-none"
              />
            </label>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {listLoading ? (
              <div className="flex min-h-[320px] items-center justify-center gap-2 text-sm text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading recruitment conversations
              </div>
            ) : listError ? (
              <div className="p-6 text-center text-sm text-red-700">{listError}</div>
            ) : filteredThreads.length === 0 ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center px-6 text-center text-slate-500">
                <Inbox className="mb-3 h-10 w-10 text-slate-300" />
                <p className="font-semibold text-slate-800">No applicant conversations found</p>
                <p className="mt-1 text-xs">Recruitment email and WhatsApp history will appear here.</p>
              </div>
            ) : filteredThreads.map((item) => (
              <ThreadRow key={item.id} thread={item} selected={thread?.id === item.id} onSelect={selectThread} />
            ))}
          </div>
        </section>

        <section className={`${thread ? 'flex' : 'hidden md:flex'} min-h-0 flex-col rounded-lg border border-slate-200 bg-slate-50 shadow-sm md:h-full`}>
          {!threadParam ? (
            <div className="flex h-full min-h-[420px] flex-col items-center justify-center p-8 text-center text-slate-500">
              <Mail className="mb-3 h-12 w-12 text-slate-300" />
              <p className="font-semibold text-slate-900">Select an applicant conversation</p>
              <p className="mt-1 max-w-md text-sm">Recruitment email and WhatsApp stay together here without mixing them into staff communications.</p>
            </div>
          ) : detailLoading ? (
            <div className="flex h-full min-h-[420px] items-center justify-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading conversation
            </div>
          ) : detailError || !thread ? (
            <div className="flex h-full min-h-[420px] flex-col items-center justify-center p-8 text-center">
              <AlertTriangle className="mb-3 h-10 w-10 text-red-500" />
              <p className="font-semibold text-slate-900">Conversation unavailable</p>
              <p className="mt-1 text-sm text-slate-500">{detailError}</p>
              <Button className="mt-4" variant="outline" onClick={closeThread}>Back to Recruitment Inbox</Button>
            </div>
          ) : (
            <>
              <div className="shrink-0 border-b border-slate-200 bg-white p-4">
                <Button type="button" variant="ghost" size="sm" onClick={closeThread} className="mb-3 md:hidden">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back
                </Button>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-slate-950">{displayName(thread)}</h2>
                    <p className="mt-1 text-sm text-slate-600">{thread.contact_email || 'No applicant email on this thread'}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <Badge tone="blue">Applicant recruitment</Badge>
                      <Badge tone={threadStatus(thread) === 'closed' ? 'slate' : 'green'}>{threadStatus(thread) === 'closed' ? 'Closed' : 'Open'}</Badge>
                      {activated ? <Badge tone="slate">Recruitment completed</Badge> : null}
                    </div>
                  </div>
                  {thread.entity_id ? (
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/portal/recruitment/${thread.entity_id}`}>
                        <ExternalLink className="mr-1.5 h-4 w-4" /> Open applicant record
                      </Link>
                    </Button>
                  ) : null}
                </div>

                {activated ? (
                  <div className="mt-3 rounded-md border border-slate-300 bg-slate-50 p-3 text-xs text-slate-700">
                    <strong>Recruitment history is read-only.</strong> This applicant has been activated as staff. New staff communication must not be sent from the Recruitment Inbox.
                  </div>
                ) : !applicant ? (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                    <strong>Sending is temporarily disabled.</strong> CareOps could not confirm this applicant's current recruitment/activation status. Refresh the conversation before sending anything.
                  </div>
                ) : null}
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-4">
                {messages.length === 0 ? (
                  <div className="flex min-h-[260px] items-center justify-center text-sm text-slate-500">No messages in this recruitment thread yet.</div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((message) => <MessageCard key={message.id} message={message} />)}
                  </div>
                )}
              </div>

              {canSendApplicantCommunication ? (
                <div className="shrink-0 overflow-y-auto border-t border-slate-200 bg-white p-3 md:max-h-[52%]" data-testid="recruitment-message-controls">
                  <div className="mb-3 flex flex-wrap gap-2" role="tablist" aria-label="Recruitment message type">
                    <Button
                      type="button"
                      size="sm"
                      variant={mode === 'manual' ? 'default' : 'outline'}
                      onClick={() => { setMode('manual'); setSendNotice(null); }}
                    >
                      <MessageCircle className="mr-1.5 h-4 w-4" /> Start message
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={mode === 'guided_email' ? 'default' : 'outline'}
                      onClick={() => { setMode('guided_email'); setSendNotice(null); }}
                      disabled={!thread.entity_id}
                    >
                      <CheckCircle2 className="mr-1.5 h-4 w-4" /> Guided email
                    </Button>
                  </div>

                  {mode === 'manual' ? (
                    <PersonMessageComposer
                      domain="recruitment"
                      employeeId={thread.entity_id}
                      token={token}
                      messages={messages}
                      threadId={thread.id}
                      emailEnabled
                      readOnly={false}
                      initialChannel={messages.some((message) => String(message?.channel || '').toLowerCase() === 'whatsapp') ? 'whatsapp' : 'email'}
                      onSent={() => refreshThreadDeliveryStatus(thread.id)}
                    />
                  ) : null}

                  {mode === 'guided_email' ? (
                    <div className="space-y-3">
                      <div>
                        <Label>Recruitment guidance template</Label>
                        <Select value={selectedEmailTemplate} onValueChange={setSelectedEmailTemplate}>
                          <SelectTrigger className="mt-2"><SelectValue placeholder="Choose an onboarding message" /></SelectTrigger>
                          <SelectContent>
                            {templates.map((item) => <SelectItem key={item.key} value={item.key}>{item.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      {selectedEmailTemplate ? (
                        <>
                          <div>
                            <Label htmlFor="guided-email-subject">Subject</Label>
                            <Input id="guided-email-subject" className="mt-2" value={guidedSubject} onChange={(event) => setGuidedSubject(event.target.value)} />
                          </div>
                          <div>
                            <Label htmlFor="guided-email-body">Message</Label>
                            <Textarea id="guided-email-body" className="mt-2" rows={6} value={guidedBody} onChange={(event) => setGuidedBody(event.target.value)} />
                          </div>
                          {selectedTemplate?.action_label ? (
                            <p className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                              Secure CareOps action: <strong>{selectedTemplate.action_label}</strong>. The secure link is generated only when the email is sent.
                            </p>
                          ) : null}
                          <div className="flex justify-end">
                            <Button type="button" onClick={sendGuidedEmail} disabled={sending || !guidedSubject.trim() || !guidedBody.trim()}>
                              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                              Send guided email
                            </Button>
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-950">
                    <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>Keep sensitive evidence inside CareOps. Do not ask applicants to email or WhatsApp passports, DBS, Right to Work, health information or other sensitive recruitment evidence.</p>
                  </div>

                  {sendNotice ? (
                    <div className={`mt-3 rounded-md border p-3 text-sm ${
                      sendNotice.type === 'success'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : sendNotice.type === 'warning'
                          ? 'border-amber-200 bg-amber-50 text-amber-900'
                          : 'border-red-200 bg-red-50 text-red-800'
                    }`} role="status">
                      {sendNotice.text}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
